import type { ReactNode } from 'react';
import { StoreButtons } from '../components/StoreButtons';
import { site } from '../config/site';
import { useReveal } from '../hooks/useReveal';

function Reveal({
  className = '',
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`reveal ${className}`.trim()}>
      {children}
    </div>
  );
}

function RevealStep({
  className = '',
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const ref = useReveal<HTMLLIElement>();
  return (
    <li ref={ref} className={`reveal lp-flow__item ${className}`.trim()}>
      {children}
    </li>
  );
}

const STEPS = [
  {
    n: '01',
    title: 'Pick a program',
    body: 'Morning Ignite, Core Forge, Leg Engine — short guided sessions with an alien coach.',
    shot: '/screens/1-home.png',
    alt: 'Workouts home with today’s program',
  },
  {
    n: '02',
    title: 'Train with form cues',
    body: 'Follow loopable motions, spoken reps, and fixed camera angles — Front, ¾, Side.',
    shot: '/screens/3-workout.png',
    alt: 'Workouts session with alien coach',
  },
  {
    n: '03',
    title: 'Build your streak',
    body: 'Log sets and reps, keep history, and come back tomorrow with a clear next session.',
    shot: '/screens/4-history.png',
    alt: 'Workouts history and streak',
  },
] as const;

const PLANS = [
  {
    label: 'Start free',
    name: '7-day trial',
    price: '$0',
    period: 'then choose a plan',
    featured: false,
    cta: 'Start free',
    points: [
      'Full Premium unlock',
      'Alien coach + voice cues',
      'Reminder before it ends',
    ],
  },
  {
    label: 'Best value',
    name: 'Yearly',
    price: '$29.99',
    period: '/ year',
    featured: true,
    cta: 'Get Yearly',
    points: [
      'Full program library',
      'Alien coach + voice cues',
      'Synced history & streaks',
    ],
  },
  {
    label: 'Flexible',
    name: 'Monthly',
    price: '$4.99',
    period: '/ month',
    featured: false,
    cta: 'Get Monthly',
    points: [
      'Full program library',
      'Alien coach + voice cues',
      'Cancel anytime in store settings',
    ],
  },
] as const;

export function LandingPage() {
  return (
    <>
      <section className="lp-hero" id="download">
        <div className="lp-hero__media" aria-hidden>
          <img
            src="/screens/1-home.png"
            alt=""
            className="lp-hero__img"
          />
          <div className="lp-hero__veil" />
          <div className="lp-hero__glow" />
        </div>
        <div className="wrap lp-hero__inner">
          <p className="lp-hero__brand">
            <img src={site.logoUrl} alt="" width={64} height={64} />
            <span>Workouts</span>
          </p>
          <h1>Train with an alien coach.</h1>
          <p className="lp-hero__lead">
            Home sessions for belly fat, abs, and strength — guided moves,
            spoken cues, and a streak that sticks.
          </p>
          <div className="lp-hero__cta">
            <StoreButtons />
          </div>
        </div>
        <a className="lp-hero__scroll" href="#loop" aria-label="How it works">
          <span />
        </a>
      </section>

      <section id="loop" className="lp-section">
        <div className="wrap">
          <Reveal>
            <p className="lp-kicker">How it works</p>
            <h2 className="lp-title">Three steps. One clearer session.</h2>
            <p className="lp-sub">
              Choose a program, follow the coach, log the work.
            </p>
          </Reveal>
          <ol className="lp-flow">
            {STEPS.map((s, i) => (
              <RevealStep
                key={s.n}
                className={`reveal-delay-${i + 1}${i % 2 === 1 ? ' lp-flow__item--flip' : ''}`}
              >
                <div className="lp-flow__copy">
                  <span className="lp-flow__n">{s.n}</span>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </div>
                <div className="lp-phone">
                  <div className="lp-phone__bezel">
                    <img src={s.shot} alt={s.alt} loading="lazy" />
                  </div>
                </div>
              </RevealStep>
            ))}
          </ol>
        </div>
      </section>

      <section id="glimpse" className="lp-status">
        <div className="lp-status__media" aria-hidden>
          <img src="/screens/2-workouts.png" alt="" />
        </div>
        <div className="lp-status__veil" aria-hidden />
        <div className="wrap lp-status__copy">
          <Reveal>
            <p className="lp-kicker lp-kicker--on-media">Programs</p>
            <h2 className="lp-title">Train every muscle on purpose.</h2>
            <p className="lp-sub">
              Chest, arms, core, legs — pick a focus, open today’s session, and
              follow the coach without guessing what to do next.
            </p>
          </Reveal>
        </div>
      </section>

      <section id="plans" className="lp-section lp-section--plans">
        <div className="wrap">
          <Reveal>
            <p className="lp-kicker">Plans</p>
            <h2 className="lp-title">Try free. Then Monthly or Yearly.</h2>
            <p className="lp-sub">
              Auto-renewable Premium via the App Store or Google Play. Cancel
              anytime in store settings.
            </p>
          </Reveal>
          <div className="lp-plans">
            {PLANS.map((plan, i) => (
              <Reveal key={plan.name} className={`reveal-delay-${i + 1}`}>
                <a
                  className={`lp-plan${plan.featured ? ' lp-plan--featured' : ''}`}
                  href="#download"
                >
                  <span className="lp-plan__label">{plan.label}</span>
                  <h3>{plan.name}</h3>
                  <p className="lp-plan__price">
                    {plan.price} <span>{plan.period}</span>
                  </p>
                  <ul>
                    {plan.points.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                  <span className="lp-plan__cta">{plan.cta}</span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-close">
        <div className="wrap">
          <Reveal>
            <div className="lp-close__inner">
              <img src={site.logoUrl} alt="" width={72} height={72} />
              <h2>Workouts</h2>
              <p>Train with an alien coach.</p>
              <StoreButtons />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
